/**
 * CoachPlaybook - Playbook Component
 * Version: 0.5.145
 * Description: Carousel-style play browser with smooth transitions
 * 
 * v0.5.118 Changes:
 * - Added Out of Bounds as 4th play type (purple #8b5cf6)
 * - OOB categories: BLOB, SLOB, Full Court Inbound, After Timeout
 * - PWA support (manifest.json, service-worker.js, icons)
 * - Visual Polish: Enhanced transitions, shadows, spacing
 * - Improved filter pills with 2px borders, better padding
 * - Cards with softer shadows and rounded corners (14px/16px)
 * - Better typography with letter-spacing
 * 
 * v0.5.117 Changes:
 * - "All" filter pill now green (#22c55e) to differentiate from Offense
 * - Multi-select category filters - can select multiple categories at once
 * 
 * v0.5.116 Changes:
 * - New pill-style type filters (All | Offense | Defense | Transition) with counts
 * - Category sub-filters appear when type is selected
 * - Removed green "selected" check logic from all cards
 * - Added subtle "Recent" badge for last edited play
 * 
 * v0.5.101 Changes:
 * - Version sync with PlayDesigner updates
 * 
 * v0.5.100 Changes:
 * - Added comprehensive data-element attributes for automation testing
 * 
 * v0.5.66 Changes:
 * - Professional header with gradient, better typography, SVG icons
 * - Professional footer with keyboard hints (kbd styling)
 * - Play count/filter status in header subtitle
 * - Improved button styling with shadows and transitions
 * - Better carousel background gradient
 */

import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { PLAY_TYPES, PLAY_CATEGORIES, PLAY_SITUATIONS, PLAY_TAGS, filterPlays } from '../../utils/plays';

// Mini Court Preview Component - renders a small preview of the play
const MiniCourtPreview = ({ play, style }) => {
  const positions = play.positions || [];
  const paths = play.paths || [];
  const passes = play.passes || [];
  const screens = play.screens || [];
  const shots = play.shots || []; // v0.5.44: Add shots
  
  // Default position colors
  const positionColors = {
    PG: '#fbbf24', // Yellow
    SG: '#10b981', // Green  
    SF: '#f97316', // Orange
    PF: '#a855f7', // Brown/Purple
    C: '#8b5cf6'   // Purple
  };
  
  return (
    <div style={{ 
      width: '100%', 
      height: '100%', 
      backgroundColor: '#f8fafc',
      borderRadius: '8px',
      overflow: 'hidden',
      position: 'relative',
      ...style 
    }}>
      <svg viewBox="0 0 100 85" style={{ width: '100%', height: '100%' }}>
        {/* Court outline */}
        <rect x="5" y="2" width="90" height="80" fill="none" stroke="#cbd5e1" strokeWidth="0.5" />
        
        {/* Three-point arc */}
        <path d="M 12 2 L 12 20 Q 12 55 50 55 Q 88 55 88 20 L 88 2" 
          fill="none" stroke="#cbd5e1" strokeWidth="0.5" />
        
        {/* Key/Paint */}
        <rect x="31" y="2" width="38" height="35" fill="none" stroke="#cbd5e1" strokeWidth="0.5" />
        
        {/* Free throw circle */}
        <circle cx="50" cy="37" r="11" fill="none" stroke="#cbd5e1" strokeWidth="0.5" strokeDasharray="2,2" />
        
        {/* Basket */}
        <circle cx="50" cy="8" r="3" fill="none" stroke="#94a3b8" strokeWidth="0.8" />
        <rect x="43" y="3" width="14" height="1.5" fill="#94a3b8" />
        
        {/* Movement paths */}
        {paths.map((path, idx) => {
          if (!path.points || path.points.length < 2) return null;
          const pathStr = path.points.map((p, i) => 
            `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y * 0.85}`
          ).join(' ');
          return (
            <path 
              key={`path-${idx}`}
              d={pathStr}
              fill="none"
              stroke={path.color || '#6b7280'}
              strokeWidth="1.2"
              strokeDasharray="3,2"
              opacity="0.7"
            />
          );
        })}
        
        {/* Pass lines */}
        {passes.map((pass, idx) => {
          const fromPos = positions.find(p => p.id === pass.fromPositionId);
          const toPos = positions.find(p => p.id === pass.toPositionId);
          if (!fromPos || !toPos || fromPos.startX === null || toPos.startX === null) return null;
          return (
            <line
              key={`pass-${idx}`}
              x1={fromPos.startX}
              y1={fromPos.startY * 0.85}
              x2={toPos.startX}
              y2={toPos.startY * 0.85}
              stroke="#3b82f6"
              strokeWidth="1"
              opacity="0.6"
              markerEnd="url(#arrowhead)"
            />
          );
        })}
        
        {/* Arrow marker definition */}
        <defs>
          <marker id="arrowhead" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
            <path d="M0,0 L6,3 L0,6" fill="none" stroke="#3b82f6" strokeWidth="1" />
          </marker>
        </defs>
        
        {/* Screens */}
        {screens.map((screen, idx) => (
          <rect
            key={`screen-${idx}`}
            x={screen.x - 3}
            y={screen.y * 0.85 - 1}
            width="6"
            height="2"
            fill="#374151"
            transform={`rotate(${screen.rotation || 0}, ${screen.x}, ${screen.y * 0.85})`}
            opacity="0.7"
          />
        ))}
        
        {/* v0.5.44: Shot arcs */}
        {shots.map((shot, idx) => {
          const shooter = positions.find(p => p.id === shot.shooterId);
          if (!shooter || shooter.startX === null) return null;
          
          // Get shooter position at the step before the shot
          const shooterX = shooter.startX;
          const shooterY = shooter.startY * 0.85;
          const hoopX = 50;
          const hoopY = 8;
          
          // Simple arc to hoop
          const arcPeak = Math.max(hoopY + 3, (shooterY + hoopY) / 2 - 5);
          const d = `M ${shooterX},${shooterY} Q ${(shooterX + hoopX) / 2},${arcPeak} ${hoopX},${hoopY}`;
          
          return (
            <g key={`shot-${idx}`}>
              <path
                d={d}
                fill="none"
                stroke="#f59e0b"
                strokeWidth="1"
                strokeDasharray="2,1"
                opacity="0.8"
              />
              {/* Basketball emoji at arc peak */}
              <text 
                x={(shooterX + hoopX) / 2} 
                y={arcPeak} 
                fontSize="5" 
                textAnchor="middle"
              >🏀</text>
            </g>
          );
        })}
        
        {/* Player positions */}
        {positions.filter(p => p.startX !== null && p.startY !== null).map((pos, idx) => {
          const color = pos.color || positionColors[pos.positionLabel] || '#6b7280';
          return (
            <g key={`player-${idx}`}>
              {/* Player circle */}
              <circle
                cx={pos.startX}
                cy={pos.startY * 0.85}
                r="4.5"
                fill={color}
                stroke="#fff"
                strokeWidth="1"
              />
              {/* Player label */}
              <text
                x={pos.startX}
                y={pos.startY * 0.85 + 1.5}
                fontSize="4"
                fill="#fff"
                textAnchor="middle"
                fontWeight="bold"
              >
                {pos.positionLabel}
              </text>
              {/* Ball indicator */}
              {pos.hasBall && (
                <circle
                  cx={pos.startX + 4}
                  cy={pos.startY * 0.85 - 3}
                  r="2"
                  fill="#f97316"
                  stroke="#fff"
                  strokeWidth="0.5"
                />
              )}
            </g>
          );
        })}
        
        {/* "No players" message if empty */}
        {positions.filter(p => p.startX !== null).length === 0 && (
          <text x="50" y="45" fontSize="8" fill="#9ca3af" textAnchor="middle">
            No players placed
          </text>
        )}
      </svg>
    </div>
  );
};

const PlayLibrary = ({
  plays = [],
  selectedPlay = null,
  onSelectPlay,
  onCreateNew,
  onImportPlay,
  onDuplicatePlay,
  onDeletePlay,
  onExportPlay,
  isLoading = false
}) => {
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterCategories, setFilterCategories] = useState([]); // Array for multi-select
  const [showImportModal, setShowImportModal] = useState(false);
  const [importText, setImportText] = useState('');
  const [importError, setImportError] = useState('');
  const [viewMode, setViewMode] = useState('carousel'); // 'carousel' | 'list' | 'grid'
  const [focusedIndex, setFocusedIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const fileInputRef = useRef(null);
  const carouselRef = useRef(null);
  const touchStartX = useRef(null);
  
  // Filter plays - handle category array for multi-select
  const filteredPlays = useMemo(() => {
    let filtered = filterPlays(plays, {
      search,
      type: filterType,
      category: '' // Handle category separately for array support
    });
    
    // Apply category filter if any selected
    if (filterCategories.length > 0) {
      filtered = filtered.filter(p => filterCategories.includes(p.category));
    }
    
    return filtered;
  }, [plays, search, filterType, filterCategories]);
  
  // Reset focus index when filtered plays change
  useEffect(() => {
    if (focusedIndex >= filteredPlays.length) {
      setFocusedIndex(Math.max(0, filteredPlays.length - 1));
    }
  }, [filteredPlays.length, focusedIndex]);
  
  // Group plays by type (for list view)
  const groupedPlays = useMemo(() => {
    const groups = { offense: [], defense: [], transition: [], outofbounds: [] };
    filteredPlays.forEach(play => {
      if (groups[play.type]) groups[play.type].push(play);
      else groups.offense.push(play);
    });
    return groups;
  }, [filteredPlays]);
  
  // Carousel navigation - Netflix-style smooth scroll
  const goToNext = useCallback(() => {
    if (filteredPlays.length <= 1 || isAnimating) return;
    setIsAnimating(true);
    setFocusedIndex(prev => (prev + 1) % filteredPlays.length);
    setTimeout(() => setIsAnimating(false), 400);
  }, [filteredPlays.length, isAnimating]);
  
  const goToPrev = useCallback(() => {
    if (filteredPlays.length <= 1 || isAnimating) return;
    setIsAnimating(true);
    setFocusedIndex(prev => (prev - 1 + filteredPlays.length) % filteredPlays.length);
    setTimeout(() => setIsAnimating(false), 400);
  }, [filteredPlays.length, isAnimating]);
  
  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (viewMode !== 'carousel') return;
      if (e.key === 'ArrowRight') goToNext();
      if (e.key === 'ArrowLeft') goToPrev();
      if (e.key === 'Enter' && filteredPlays[focusedIndex]) {
        onSelectPlay(filteredPlays[focusedIndex]);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [viewMode, goToNext, goToPrev, focusedIndex, filteredPlays, onSelectPlay]);
  
  // Touch/swipe handlers
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };
  
  const handleTouchEnd = (e) => {
    if (!touchStartX.current) return;
    const touchEndX = e.changedTouches[0].clientX;
    const diff = touchStartX.current - touchEndX;
    if (Math.abs(diff) > 50) { // Minimum swipe distance
      if (diff > 0) goToNext();
      else goToPrev();
    }
    touchStartX.current = null;
  };
  
  // Handle file upload
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      setImportText(e.target.result);
      setImportError('');
    };
    reader.onerror = () => setImportError('Failed to read file');
    reader.readAsText(file);
  };
  
  const handleImport = () => {
    if (!importText.trim()) {
      setImportError('Please paste JSON or upload a file');
      return;
    }
    try {
      const playData = JSON.parse(importText);
      if (!playData.positions || !Array.isArray(playData.positions)) {
        setImportError('Invalid play format');
        return;
      }
      if (onImportPlay) {
        onImportPlay(playData);
        setShowImportModal(false);
        setImportText('');
        setImportError('');
      }
    } catch (err) {
      setImportError('Invalid JSON format');
    }
  };
  
  const getCategoryLabel = (play) => {
    const categories = PLAY_CATEGORIES[play.type] || [];
    const cat = categories.find(c => c.id === play.category);
    return cat?.label || play.category || 'Uncategorized';
  };
  
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };
  
  const getTypeIcon = (type) => {
    switch(type) {
      case 'offense': return '🏀';
      case 'defense': return '🛡️';
      case 'transition': return '⚡';
      case 'outofbounds': return '📍';
      default: return '📋';
    }
  };
  
  return (
    <div style={styles.container} data-element="playbook-root">
      {/* v0.5.105: Professional Dark Header */}
      <div style={styles.header} data-element="playbook-header">
        <div style={styles.headerLeft} data-element="playbook-header-left">
          <div style={styles.titleSection} data-element="playbook-title-section">
            <div style={styles.titleIcon} data-element="playbook-header-icon">📋</div>
            <div data-element="playbook-title-text">
              <h3 style={styles.title} data-element="playbook-header-title">Playbook</h3>
              <span style={styles.subtitle} data-element="playbook-header-subtitle">{plays.length} plays • {filteredPlays.length !== plays.length ? `${filteredPlays.length} filtered` : 'All plays'}</span>
            </div>
          </div>
        </div>
        <div style={styles.headerRight} data-element="playbook-header-right">
          {/* View mode toggle */}
          <div style={styles.viewToggle} data-element="playbook-view-toggle">
            <button
              data-element="playbook-view-carousel-btn"
              style={{
                ...styles.viewToggleBtn,
                backgroundColor: viewMode === 'carousel' ? '#3b82f6' : 'transparent',
                color: viewMode === 'carousel' ? '#fff' : '#94a3b8',
                boxShadow: viewMode === 'carousel' ? '0 2px 6px rgba(59,130,246,0.3)' : 'none'
              }}
              onClick={() => setViewMode('carousel')}
              title="Carousel View"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="2" y="6" width="20" height="12" rx="2"/>
                <circle cx="12" cy="12" r="3"/>
              </svg>
            </button>
            <button
              data-element="playbook-view-list-btn"
              style={{
                ...styles.viewToggleBtn,
                backgroundColor: viewMode === 'list' ? '#3b82f6' : 'transparent',
                color: viewMode === 'list' ? '#fff' : '#94a3b8',
                boxShadow: viewMode === 'list' ? '0 2px 6px rgba(59,130,246,0.3)' : 'none'
              }}
              onClick={() => setViewMode('list')}
              title="List View"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="3" y1="6" x2="21" y2="6"/>
                <line x1="3" y1="12" x2="21" y2="12"/>
                <line x1="3" y1="18" x2="21" y2="18"/>
              </svg>
            </button>
            <button
              data-element="playbook-view-grid-btn"
              style={{
                ...styles.viewToggleBtn,
                backgroundColor: viewMode === 'grid' ? '#3b82f6' : 'transparent',
                color: viewMode === 'grid' ? '#fff' : '#94a3b8',
                boxShadow: viewMode === 'grid' ? '0 2px 6px rgba(59,130,246,0.3)' : 'none'
              }}
              onClick={() => setViewMode('grid')}
              title="Grid View"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="7" height="7"/>
                <rect x="14" y="3" width="7" height="7"/>
                <rect x="3" y="14" width="7" height="7"/>
                <rect x="14" y="14" width="7" height="7"/>
              </svg>
            </button>
          </div>
          <button style={styles.createButton} onClick={onCreateNew} data-element="playbook-new-play-btn">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{marginRight: '6px'}}>
              <line x1="12" y1="5" x2="12" y2="19"/>
              <line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            New Play
          </button>
          <button style={styles.importButton} onClick={() => setShowImportModal(true)} title="Import Play" data-element="playbook-import-btn">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{marginRight: '4px'}}>
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="7 10 12 15 17 10"/>
              <line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
            Import
          </button>
        </div>
      </div>
      
      {/* Search */}
      <div style={styles.searchContainer} data-element="playbook-search-container">
        <input
          type="text"
          placeholder="Search plays..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={styles.searchInput}
          data-element="playbook-search-input"
        />
        <button
          style={styles.importButton}
          onClick={() => setShowImportModal(true)}
          data-element="playbook-import-btn"
          title="Import play"
        >
          📥
        </button>
      </div>
      
      {/* Type Filter Pills - styled like section headers */}
      <div style={styles.filterPillsContainer} data-element="playbook-filter-pills">
        <div style={styles.filterPillsRow} data-element="playbook-type-filters">
          <button
            data-element="playbook-filter-all"
            style={{
              ...styles.filterPill,
              backgroundColor: filterType === '' ? '#22c55e' : 'transparent',
              color: filterType === '' ? '#fff' : '#94a3b8',
              borderColor: filterType === '' ? '#22c55e' : '#475569'
            }}
            onClick={() => { setFilterType(''); setFilterCategories([]); }}
          >
            All
            <span style={styles.filterPillCount}>{plays.length}</span>
          </button>
          <button
            data-element="playbook-filter-offense"
            style={{
              ...styles.filterPill,
              backgroundColor: filterType === 'offense' ? '#3b82f6' : 'transparent',
              color: filterType === 'offense' ? '#fff' : '#3b82f6',
              borderColor: filterType === 'offense' ? '#3b82f6' : '#3b82f640'
            }}
            onClick={() => { setFilterType(filterType === 'offense' ? '' : 'offense'); setFilterCategories([]); }}
          >
            🏀 Offense
            <span style={styles.filterPillCount}>{groupedPlays.offense.length}</span>
          </button>
          <button
            data-element="playbook-filter-defense"
            style={{
              ...styles.filterPill,
              backgroundColor: filterType === 'defense' ? '#dc2626' : 'transparent',
              color: filterType === 'defense' ? '#fff' : '#dc2626',
              borderColor: filterType === 'defense' ? '#dc2626' : '#dc262640'
            }}
            onClick={() => { setFilterType(filterType === 'defense' ? '' : 'defense'); setFilterCategories([]); }}
          >
            🛡️ Defense
            <span style={styles.filterPillCount}>{groupedPlays.defense.length}</span>
          </button>
          <button
            data-element="playbook-filter-transition"
            style={{
              ...styles.filterPill,
              backgroundColor: filterType === 'transition' ? '#f59e0b' : 'transparent',
              color: filterType === 'transition' ? '#fff' : '#f59e0b',
              borderColor: filterType === 'transition' ? '#f59e0b' : '#f59e0b40'
            }}
            onClick={() => { setFilterType(filterType === 'transition' ? '' : 'transition'); setFilterCategories([]); }}
          >
            ⚡ Transition
            <span style={styles.filterPillCount}>{groupedPlays.transition.length}</span>
          </button>
          <button
            data-element="playbook-filter-outofbounds"
            style={{
              ...styles.filterPill,
              backgroundColor: filterType === 'outofbounds' ? '#8b5cf6' : 'transparent',
              color: filterType === 'outofbounds' ? '#fff' : '#8b5cf6',
              borderColor: filterType === 'outofbounds' ? '#8b5cf6' : '#8b5cf640'
            }}
            onClick={() => { setFilterType(filterType === 'outofbounds' ? '' : 'outofbounds'); setFilterCategories([]); }}
          >
            📍 Out of Bounds
            <span style={styles.filterPillCount}>{groupedPlays.outofbounds.length}</span>
          </button>
        </div>
        
        {/* Category sub-filters - only show when type is selected */}
        {filterType && PLAY_CATEGORIES[filterType]?.length > 0 && (
          <div style={styles.filterPillsRow} data-element="playbook-category-filters">
            <span style={styles.categoryLabel}>Category:</span>
            <button
              data-element="playbook-filter-category-all"
              style={{
                ...styles.categoryPill,
                backgroundColor: filterCategories.length === 0 ? '#64748b' : 'transparent',
                color: filterCategories.length === 0 ? '#fff' : '#94a3b8'
              }}
              onClick={() => setFilterCategories([])}
            >
              All
            </button>
            {PLAY_CATEGORIES[filterType].map(cat => {
              const isSelected = filterCategories.includes(cat.id);
              const actualCount = plays.filter(p => p.type === filterType && p.category === cat.id).length;
              return (
                <button
                  key={cat.id}
                  data-element={`playbook-filter-category-${cat.id}`}
                  style={{
                    ...styles.categoryPill,
                    backgroundColor: isSelected ? '#64748b' : 'transparent',
                    color: isSelected ? '#fff' : '#94a3b8'
                  }}
                  onClick={() => {
                    if (isSelected) {
                      // Remove from array
                      setFilterCategories(filterCategories.filter(c => c !== cat.id));
                    } else {
                      // Add to array
                      setFilterCategories([...filterCategories, cat.id]);
                    }
                  }}
                >
                  {cat.label}
                  {actualCount > 0 && <span style={styles.categoryPillCount}>{actualCount}</span>}
                </button>
              );
            })}
          </div>
        )}
      </div>
      
      {/* Main Content Area */}
      <div style={styles.mainContent} data-element="playbook-main-content">
        {isLoading ? (
          <div style={styles.emptyState} data-element="playbook-loading">Loading plays...</div>
        ) : filteredPlays.length === 0 ? (
          <div style={styles.emptyState} data-element="playbook-empty-state">
            {plays.length === 0 ? (
              <>
                <span style={styles.emptyIcon}>📋</span>
                <span>No plays yet</span>
                <div style={styles.emptyButtons}>
                  <button style={styles.emptyButton} onClick={onCreateNew} data-element="playbook-empty-create-btn">Create your first play</button>
                  <button style={{...styles.emptyButton, backgroundColor: '#6b7280'}} onClick={() => setShowImportModal(true)} data-element="playbook-empty-import-btn">Import a play</button>
                </div>
              </>
            ) : (
              <>
                <span>No plays match your search</span>
                <button style={styles.emptyButton} onClick={() => { setSearch(''); setFilterType(''); setFilterCategories([]); }} data-element="playbook-empty-clear-btn">Clear filters</button>
              </>
            )}
          </div>
        ) : viewMode === 'carousel' ? (
          /* ===== CAROUSEL VIEW ===== */
          <div 
            style={styles.carouselContainer}
            ref={carouselRef}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            data-element="playbook-carousel"
          >
            {/* Left Arrow */}
            <button
              style={{
                ...styles.carouselArrow,
                left: '20px',
                opacity: filteredPlays.length <= 1 ? 0.3 : 1
              }}
              onClick={goToPrev}
              disabled={filteredPlays.length <= 1}
              data-element="playbook-carousel-prev-btn"
            >
              ◀
            </button>
            
            {/* Carousel Track */}
            <div style={styles.carouselTrack} data-element="playbook-carousel-track">
              <div 
                style={styles.carouselInner}
                data-element="playbook-carousel-inner"
              >
                {/* Netflix-style: Render ALL plays with stable keys, position based on focusedIndex */}
                {filteredPlays.map((play, index) => {
                  const total = filteredPlays.length;
                  
                  // Calculate offset from focused card (handling wrap-around)
                  let offset = index - focusedIndex;
                  // Handle circular wrapping for smoother infinite scroll feel
                  if (offset > total / 2) offset -= total;
                  if (offset < -total / 2) offset += total;
                  
                  const distance = Math.abs(offset);
                  
                  // Only render cards within visible range (±3)
                  if (distance > 3) return null;
                  
                  const isFocused = offset === 0;
                  const isLastEdited = selectedPlay?.id === play.id;
                  
                  // Position cards using translateX based on offset
                  const cardWidth = 340;
                  const gap = 24;
                  const xPosition = offset * (cardWidth + gap);
                  
                  return (
                    <div
                      key={play.id}
                      data-element={`playbook-carousel-card-${play.id}`}
                      data-play-id={play.id}
                      data-focused={isFocused}
                      style={{
                        ...styles.carouselCard,
                        position: 'absolute',
                        left: '50%',
                        transform: `translateX(calc(-50% + ${xPosition}px)) scale(${isFocused ? 1.05 : Math.max(0.85, 1 - distance * 0.08)})`,
                        opacity: distance > 2 ? 0 : (isFocused ? 1 : Math.max(0.5, 0.9 - distance * 0.2)),
                        zIndex: isFocused ? 10 : 5 - distance,
                        transition: 'transform 0.4s cubic-bezier(0.25, 0.1, 0.25, 1), opacity 0.4s ease'
                      }}
                      onClick={() => {
                        if (isFocused) {
                          onSelectPlay(play);
                        } else if (!isAnimating) {
                          // Navigate to this card
                          setIsAnimating(true);
                          setFocusedIndex(index);
                          setTimeout(() => setIsAnimating(false), 400);
                        }
                      }}
                    >
                      {/* Last Edited Badge - subtle indicator */}
                      {isLastEdited && (
                        <div 
                          data-element={`playbook-card-${play.id}-last-edited`}
                          style={{
                            position: 'absolute',
                            top: '8px',
                            right: '8px',
                            backgroundColor: 'rgba(100,116,139,0.9)',
                            color: '#fff',
                            padding: '3px 8px',
                            borderRadius: '6px',
                            fontSize: '9px',
                            fontWeight: '600',
                            zIndex: 20,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                        >
                          <span style={{ fontSize: '10px' }}>✎</span> Recent
                        </div>
                      )}
                      
                      {/* Card Header */}
                      <div 
                        data-element={`playbook-card-${play.id}-header`}
                        style={{
                        ...styles.carouselCardHeader,
                        backgroundColor: play.type === 'offense' ? '#3b82f6' : play.type === 'defense' ? '#dc2626' : play.type === 'transition' ? '#f59e0b' : '#8b5cf6'
                      }}>
                        <span style={styles.carouselCardType} data-element={`playbook-card-${play.id}-type`}>{getTypeIcon(play.type)} {play.type}</span>
                        <span style={styles.carouselCardCategory} data-element={`playbook-card-${play.id}-category`}>{getCategoryLabel(play)}</span>
                      </div>
                      
                      {/* Card Body */}
                      <div style={styles.carouselCardBody} data-element={`playbook-card-${play.id}-body`}>
                        <h4 style={styles.carouselCardTitle} data-element={`playbook-card-${play.id}-title`}>{play.name || 'Untitled Play'}</h4>
                        
                        {/* Mini Court Preview with actual play diagram */}
                        <div data-element={`playbook-card-${play.id}-preview`}>
                          <MiniCourtPreview play={play} style={styles.miniCourtPreview} />
                        </div>
                        
                        {/* Play Stats */}
                        <div style={styles.carouselCardStats} data-element={`playbook-card-${play.id}-stats`}>
                          <span data-element={`playbook-card-${play.id}-stat-players`}>👥 {play.positions?.filter(p => p.startX !== null).length || 0}</span>
                          <span data-element={`playbook-card-${play.id}-stat-paths`}>📍 {play.paths?.length || 0}</span>
                          <span data-element={`playbook-card-${play.id}-stat-passes`}>➡️ {play.passes?.length || 0}</span>
                          <span data-element={`playbook-card-${play.id}-stat-screens`}>🛡️ {play.screens?.length || 0}</span>
                        </div>
                      </div>
                      
                      {/* Card Footer - Actions */}
                      {isFocused && (
                        <div style={styles.carouselCardFooter} data-element={`playbook-card-${play.id}-actions`}>
                          <button style={styles.carouselActionBtn} onClick={(e) => { e.stopPropagation(); onSelectPlay(play); }} data-element={`playbook-card-${play.id}-edit-btn`}>
                            ✏️ Edit
                          </button>
                          <button style={styles.carouselActionBtn} onClick={(e) => { e.stopPropagation(); onDuplicatePlay(play); }} data-element={`playbook-card-${play.id}-copy-btn`}>
                            📋 Copy
                          </button>
                          <button style={styles.carouselActionBtn} onClick={(e) => { e.stopPropagation(); onExportPlay(play); }} data-element={`playbook-card-${play.id}-export-btn`}>
                            📤
                          </button>
                          <button style={{...styles.carouselActionBtn, color: '#dc2626'}} onClick={(e) => { e.stopPropagation(); if(window.confirm('Delete this play?')) onDeletePlay(play); }} data-element={`playbook-card-${play.id}-delete-btn`}>
                            🗑️
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            
            {/* Right Arrow */}
            <button
              style={{
                ...styles.carouselArrow,
                right: '20px',
                opacity: filteredPlays.length <= 1 ? 0.3 : 1
              }}
              onClick={goToNext}
              disabled={filteredPlays.length <= 1}
              data-element="playbook-carousel-next-btn"
            >
              ▶
            </button>
            
            {/* Pagination Dots */}
            <div style={styles.paginationDots} data-element="playbook-carousel-pagination">
              {filteredPlays.length <= 10 ? (
                filteredPlays.map((_, index) => (
                  <button
                    key={index}
                    data-element={`playbook-carousel-dot-${index}`}
                    style={{
                      ...styles.paginationDot,
                      backgroundColor: index === focusedIndex ? '#3b82f6' : '#d1d5db'
                    }}
                    onClick={() => setFocusedIndex(index)}
                  />
                ))
              ) : (
                <span style={{ fontSize: '12px', color: '#6b7280' }} data-element="playbook-carousel-counter">
                  {focusedIndex + 1} / {filteredPlays.length}
                </span>
              )}
            </div>
          </div>
        ) : viewMode === 'grid' ? (
          /* ===== GRID VIEW - Carousel-style cards in grid layout ===== */
          <div style={styles.gridContainer} data-element="playbook-grid">
            {/* Offense Section */}
            {groupedPlays.offense.length > 0 && (
              <div style={styles.gridSection} data-element="playbook-grid-section-offense">
                <div style={styles.gridSectionHeader} data-element="playbook-grid-offense-header">
                  <span style={{...styles.gridSectionTitle, color: '#3b82f6'}} data-element="playbook-grid-offense-title">🏀 Offense</span>
                  <span style={styles.gridSectionCount} data-element="playbook-grid-offense-count">{groupedPlays.offense.length}</span>
                </div>
                <div style={styles.gridCards} data-element="playbook-grid-offense-cards">
                  {groupedPlays.offense.map(play => (
                    <GridPlayCard 
                      key={play.id} 
                      play={play} 
                      isLastEdited={selectedPlay?.id === play.id}
                      onSelect={() => onSelectPlay(play)}
                      onDuplicate={() => onDuplicatePlay(play)}
                      onDelete={() => onDeletePlay(play)}
                      onExport={() => onExportPlay(play)}
                      getCategoryLabel={getCategoryLabel}
                      getTypeIcon={getTypeIcon}
                    />
                  ))}
                </div>
              </div>
            )}
            
            {/* Defense Section */}
            {groupedPlays.defense.length > 0 && (
              <div style={styles.gridSection} data-element="playbook-grid-section-defense">
                <div style={styles.gridSectionHeader} data-element="playbook-grid-defense-header">
                  <span style={{...styles.gridSectionTitle, color: '#dc2626'}} data-element="playbook-grid-defense-title">🛡️ Defense</span>
                  <span style={styles.gridSectionCount} data-element="playbook-grid-defense-count">{groupedPlays.defense.length}</span>
                </div>
                <div style={styles.gridCards} data-element="playbook-grid-defense-cards">
                  {groupedPlays.defense.map(play => (
                    <GridPlayCard 
                      key={play.id} 
                      play={play} 
                      isLastEdited={selectedPlay?.id === play.id}
                      onSelect={() => onSelectPlay(play)}
                      onDuplicate={() => onDuplicatePlay(play)}
                      onDelete={() => onDeletePlay(play)}
                      onExport={() => onExportPlay(play)}
                      getCategoryLabel={getCategoryLabel}
                      getTypeIcon={getTypeIcon}
                    />
                  ))}
                </div>
              </div>
            )}
            
            {/* Transition Section */}
            {groupedPlays.transition.length > 0 && (
              <div style={styles.gridSection} data-element="playbook-grid-section-transition">
                <div style={styles.gridSectionHeader} data-element="playbook-grid-transition-header">
                  <span style={{...styles.gridSectionTitle, color: '#f59e0b'}} data-element="playbook-grid-transition-title">⚡ Transition</span>
                  <span style={styles.gridSectionCount} data-element="playbook-grid-transition-count">{groupedPlays.transition.length}</span>
                </div>
                <div style={styles.gridCards} data-element="playbook-grid-transition-cards">
                  {groupedPlays.transition.map(play => (
                    <GridPlayCard 
                      key={play.id} 
                      play={play} 
                      isLastEdited={selectedPlay?.id === play.id}
                      onSelect={() => onSelectPlay(play)}
                      onDuplicate={() => onDuplicatePlay(play)}
                      onDelete={() => onDeletePlay(play)}
                      onExport={() => onExportPlay(play)}
                      getCategoryLabel={getCategoryLabel}
                      getTypeIcon={getTypeIcon}
                    />
                  ))}
                </div>
              </div>
            )}
            
            {/* Out of Bounds Section */}
            {groupedPlays.outofbounds.length > 0 && (
              <div style={styles.gridSection} data-element="playbook-grid-section-outofbounds">
                <div style={styles.gridSectionHeader} data-element="playbook-grid-outofbounds-header">
                  <span style={{...styles.gridSectionTitle, color: '#8b5cf6'}} data-element="playbook-grid-outofbounds-title">📍 Out of Bounds</span>
                  <span style={styles.gridSectionCount} data-element="playbook-grid-outofbounds-count">{groupedPlays.outofbounds.length}</span>
                </div>
                <div style={styles.gridCards} data-element="playbook-grid-outofbounds-cards">
                  {groupedPlays.outofbounds.map(play => (
                    <GridPlayCard 
                      key={play.id} 
                      play={play} 
                      isLastEdited={selectedPlay?.id === play.id}
                      onSelect={() => onSelectPlay(play)}
                      onDuplicate={() => onDuplicatePlay(play)}
                      onDelete={() => onDeletePlay(play)}
                      onExport={() => onExportPlay(play)}
                      getCategoryLabel={getCategoryLabel}
                      getTypeIcon={getTypeIcon}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          /* ===== LIST VIEW ===== */
          <div style={styles.playsList} data-element="playbook-list">
            {groupedPlays.offense.length > 0 && (
              <PlaySection title="🏀 Offense" plays={groupedPlays.offense} selectedPlay={selectedPlay} onSelectPlay={onSelectPlay} onDuplicatePlay={onDuplicatePlay} onDeletePlay={onDeletePlay} onExportPlay={onExportPlay} sectionType="offense" />
            )}
            {groupedPlays.defense.length > 0 && (
              <PlaySection title="🛡️ Defense" plays={groupedPlays.defense} selectedPlay={selectedPlay} onSelectPlay={onSelectPlay} onDuplicatePlay={onDuplicatePlay} onDeletePlay={onDeletePlay} onExportPlay={onExportPlay} sectionType="defense" />
            )}
            {groupedPlays.transition.length > 0 && (
              <PlaySection title="⚡ Transition" plays={groupedPlays.transition} selectedPlay={selectedPlay} onSelectPlay={onSelectPlay} onDuplicatePlay={onDuplicatePlay} onDeletePlay={onDeletePlay} onExportPlay={onExportPlay} sectionType="transition" />
            )}
            {groupedPlays.outofbounds.length > 0 && (
              <PlaySection title="📍 Out of Bounds" plays={groupedPlays.outofbounds} selectedPlay={selectedPlay} onSelectPlay={onSelectPlay} onDuplicatePlay={onDuplicatePlay} onDeletePlay={onDeletePlay} onExportPlay={onExportPlay} sectionType="outofbounds" />
            )}
          </div>
        )}
      </div>
      
      {/* v0.5.66: Professional Footer */}
      {viewMode === 'carousel' && filteredPlays.length > 0 && (
        <div style={styles.footer} data-element="playbook-footer">
          <div style={styles.footerNav} data-element="playbook-footer-nav-hint">
            <kbd style={styles.kbd} data-element="playbook-footer-kbd-left">←</kbd>
            <kbd style={styles.kbd} data-element="playbook-footer-kbd-right">→</kbd>
            <span style={styles.footerHint} data-element="playbook-footer-nav-label">Navigate</span>
          </div>
          <div style={styles.footerDivider} data-element="playbook-footer-divider-1" />
          <div style={styles.footerNav} data-element="playbook-footer-enter-hint">
            <kbd style={styles.kbd} data-element="playbook-footer-kbd-enter">Enter</kbd>
            <span style={styles.footerHint} data-element="playbook-footer-enter-label">Open play</span>
          </div>
          <div style={styles.footerDivider} data-element="playbook-footer-divider-2" />
          <div style={styles.footerNav} data-element="playbook-footer-counter-container">
            <span style={styles.footerCounter} data-element="playbook-footer-counter">{focusedIndex + 1} / {filteredPlays.length}</span>
          </div>
        </div>
      )}
      
      {/* Import Modal */}
      {showImportModal && (
        <div style={styles.modalOverlay} onClick={() => setShowImportModal(false)} data-element="playbook-import-modal-overlay">
          <div style={styles.modal} onClick={e => e.stopPropagation()} data-element="playbook-import-modal">
            <div style={styles.modalHeader} data-element="playbook-import-modal-header">
              <h3 style={styles.modalTitle} data-element="playbook-import-modal-title">📥 Import Play</h3>
              <button style={styles.modalClose} onClick={() => setShowImportModal(false)} data-element="playbook-import-modal-close-btn">✕</button>
            </div>
            <div style={styles.modalBody} data-element="playbook-import-modal-body">
              <p style={styles.modalDesc} data-element="playbook-import-modal-desc">Upload a play JSON file or paste the JSON content below.</p>
              <input ref={fileInputRef} type="file" accept=".json" onChange={handleFileUpload} style={{ display: 'none' }} data-element="playbook-import-file-input" />
              <button style={styles.uploadButton} onClick={() => fileInputRef.current?.click()} data-element="playbook-import-file-btn">
                📁 Choose File
              </button>
              <textarea
                value={importText}
                onChange={e => setImportText(e.target.value)}
                placeholder="Or paste JSON here..."
                style={styles.importTextarea}
                data-element="playbook-import-textarea"
              />
              {importError && <div style={styles.importError} data-element="playbook-import-error">{importError}</div>}
            </div>
            <div style={styles.modalFooter} data-element="playbook-import-modal-footer">
              <button style={styles.cancelButton} onClick={() => setShowImportModal(false)} data-element="playbook-import-cancel-btn">Cancel</button>
              <button style={styles.importConfirmButton} onClick={handleImport} data-element="playbook-import-confirm-btn">Import Play</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Play Section Sub-component (for list view)
const PlaySection = ({ title, plays, selectedPlay, onSelectPlay, onDuplicatePlay, onDeletePlay, onExportPlay, sectionType }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  
  return (
    <div style={styles.section} data-element={`playbook-list-section-${sectionType}`}>
      <div style={styles.sectionHeader} onClick={() => setIsCollapsed(!isCollapsed)} data-element={`playbook-section-${sectionType}-header`}>
        <span style={styles.sectionTitle} data-element={`playbook-section-${sectionType}-title`}>{title}</span>
        <span style={styles.sectionCount} data-element={`playbook-section-${sectionType}-count`}>{plays.length}</span>
        <span style={styles.collapseIcon} data-element={`playbook-section-${sectionType}-collapse-icon`}>{isCollapsed ? '▶' : '▼'}</span>
      </div>
      {!isCollapsed && (
        <div style={styles.sectionContent} data-element={`playbook-section-${sectionType}-content`}>
          {plays.map(play => (
            <PlayCard key={play.id} play={play} isLastEdited={selectedPlay?.id === play.id} onSelect={() => onSelectPlay(play)} onDuplicate={() => onDuplicatePlay(play)} onDelete={() => onDeletePlay(play)} onExport={() => onExportPlay(play)} />
          ))}
        </div>
      )}
    </div>
  );
};

// Grid Play Card - Full carousel-style card for grid view
const GridPlayCard = ({ play, isLastEdited, onSelect, onDuplicate, onDelete, onExport, getCategoryLabel, getTypeIcon }) => {
  const [showActions, setShowActions] = useState(false);
  const typeColor = play.type === 'offense' ? '#3b82f6' : play.type === 'defense' ? '#dc2626' : play.type === 'transition' ? '#f59e0b' : '#8b5cf6';
  
  return (
    <div
      data-element={`playbook-grid-card-${play.id}`}
      style={styles.gridPlayCard}
      onClick={onSelect}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
    >
      {/* Last Edited Badge - subtle indicator */}
      {isLastEdited && (
        <div 
          data-element={`playbook-grid-card-${play.id}-last-edited`}
          style={{
            position: 'absolute',
            top: '8px',
            right: '8px',
            backgroundColor: 'rgba(100,116,139,0.9)',
            color: '#fff',
            padding: '3px 8px',
            borderRadius: '6px',
            fontSize: '9px',
            fontWeight: '600',
            zIndex: 20,
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}
        >
          <span style={{ fontSize: '10px' }}>✎</span> Recent
        </div>
      )}
      
      {/* Card Header */}
      <div 
        data-element={`playbook-grid-card-${play.id}-header`}
        style={{
          ...styles.gridPlayCardHeader,
          backgroundColor: typeColor
        }}
      >
        <span style={styles.gridPlayCardType} data-element={`playbook-grid-card-${play.id}-type`}>
          {getTypeIcon(play.type)} {play.type}
        </span>
        <span style={styles.gridPlayCardCategory} data-element={`playbook-grid-card-${play.id}-category`}>
          {getCategoryLabel(play)}
        </span>
      </div>
      
      {/* Card Body */}
      <div style={styles.gridPlayCardBody} data-element={`playbook-grid-card-${play.id}-body`}>
        <h4 style={styles.gridPlayCardTitle} data-element={`playbook-grid-card-${play.id}-title`}>
          {play.name || 'Untitled Play'}
        </h4>
        
        {/* Mini Court Preview */}
        <div data-element={`playbook-grid-card-${play.id}-preview`}>
          <MiniCourtPreview play={play} style={styles.gridMiniCourtPreview} />
        </div>
        
        {/* Play Stats */}
        <div style={styles.gridPlayCardStats} data-element={`playbook-grid-card-${play.id}-stats`}>
          <span data-element={`playbook-grid-card-${play.id}-stat-players`}>👥 {play.positions?.filter(p => p.startX !== null).length || 0}</span>
          <span data-element={`playbook-grid-card-${play.id}-stat-paths`}>📍 {play.paths?.length || 0}</span>
          <span data-element={`playbook-grid-card-${play.id}-stat-passes`}>➡️ {play.passes?.length || 0}</span>
        </div>
      </div>
      
      {/* Hover Actions */}
      {showActions && (
        <div style={styles.gridPlayCardActions} data-element={`playbook-grid-card-${play.id}-actions`}>
          <button 
            style={styles.gridActionBtn} 
            onClick={(e) => { e.stopPropagation(); onSelect(); }} 
            data-element={`playbook-grid-card-${play.id}-edit-btn`}
            title="Edit"
          >
            ✏️
          </button>
          <button 
            style={styles.gridActionBtn} 
            onClick={(e) => { e.stopPropagation(); onDuplicate(); }} 
            data-element={`playbook-grid-card-${play.id}-copy-btn`}
            title="Duplicate"
          >
            📋
          </button>
          <button 
            style={styles.gridActionBtn} 
            onClick={(e) => { e.stopPropagation(); onExport(); }} 
            data-element={`playbook-grid-card-${play.id}-export-btn`}
            title="Export"
          >
            📤
          </button>
          <button 
            style={{...styles.gridActionBtn, color: '#ef4444'}} 
            onClick={(e) => { e.stopPropagation(); onDelete(); }} 
            data-element={`playbook-grid-card-${play.id}-delete-btn`}
            title="Delete"
          >
            🗑️
          </button>
        </div>
      )}
    </div>
  );
};

// Play Card Sub-component (for list view)
const PlayCard = ({ play, isLastEdited, onSelect, onDuplicate, onDelete, onExport }) => {
  const [showActions, setShowActions] = useState(false);
  
  const getCategoryLabel = () => {
    const categories = PLAY_CATEGORIES[play.type] || [];
    const cat = categories.find(c => c.id === play.category);
    return cat?.label || play.category;
  };
  
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };
  
  return (
    <div
      data-element={`playbook-play-card-${play.id}`}
      data-play-id={play.id}
      style={{ 
        ...styles.playCard,
        // v0.5.65: Improved hover state
        ':hover': { backgroundColor: '#f3f4f6' }
      }}
      onClick={onSelect}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
    >
      {/* Last edited indicator - subtle left border */}
      {isLastEdited && (
        <div 
          data-element={`playbook-play-card-${play.id}-recent-indicator`}
          style={{
          width: '3px',
          height: '100%',
          backgroundColor: '#64748b',
          borderRadius: '2px 0 0 2px',
          position: 'absolute',
          left: 0,
          top: 0
        }} />
      )}
      
      <div style={{...styles.playInfo, paddingLeft: isLastEdited ? '8px' : '0'}} data-element={`playbook-play-card-${play.id}-info`}>
        <div style={styles.playName} data-element={`playbook-play-card-${play.id}-name`}>
          {play.name || 'Untitled Play'}
          {isLastEdited && <span style={{ color: '#64748b', marginLeft: '8px', fontSize: '10px', fontWeight: '500' }} data-element={`playbook-play-card-${play.id}-recent`}>✎ Recent</span>}
        </div>
        <div style={styles.playMeta} data-element={`playbook-play-card-${play.id}-meta`}>
          <span style={{
            ...styles.playCategory,
            backgroundColor: play.type === 'offense' ? '#dbeafe' : play.type === 'defense' ? '#fee2e2' : play.type === 'transition' ? '#fef3c7' : '#ede9fe',
            color: play.type === 'offense' ? '#1d4ed8' : play.type === 'defense' ? '#dc2626' : play.type === 'transition' ? '#d97706' : '#7c3aed'
          }} data-element={`playbook-play-card-${play.id}-category`}>{getCategoryLabel()}</span>
          <span style={styles.playDate} data-element={`playbook-play-card-${play.id}-date`}>{formatDate(play.lastModified)}</span>
        </div>
      </div>
      <div style={styles.courtIndicator} data-element={`playbook-play-card-${play.id}-court-type`}>{play.courtView === 'full' ? '📐' : '◻️'}</div>
      {showActions && (
        <div style={styles.playActions} data-element={`playbook-play-card-${play.id}-actions`}>
          <button style={styles.actionButton} onClick={(e) => { e.stopPropagation(); onDuplicate(); }} title="Duplicate" data-element={`playbook-play-card-${play.id}-copy-btn`}>📋</button>
          <button style={styles.actionButton} onClick={(e) => { e.stopPropagation(); onExport(); }} title="Export" data-element={`playbook-play-card-${play.id}-export-btn`}>📤</button>
          <button style={{...styles.actionButton, color: '#dc2626'}} onClick={(e) => { e.stopPropagation(); if(window.confirm('Delete?')) onDelete(); }} title="Delete" data-element={`playbook-play-card-${play.id}-delete-btn`}>🗑️</button>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: { display: 'flex', flexDirection: 'column', height: '100%', backgroundColor: '#0f172a', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.3)' },
  
  // v0.5.105: Professional Dark Header - matches Dashboard aesthetic
  header: { 
    display: 'flex', 
    justifyContent: 'space-between', 
    alignItems: 'center', 
    padding: '16px 20px', 
    borderBottom: '1px solid #334155',
    background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)'
  },
  headerLeft: { display: 'flex', alignItems: 'center', gap: '12px' },
  headerRight: { display: 'flex', gap: '10px', alignItems: 'center' },
  titleSection: { display: 'flex', alignItems: 'center', gap: '12px' },
  titleIcon: { 
    fontSize: '28px', 
    filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
    width: '48px',
    height: '48px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
    borderRadius: '12px',
    boxShadow: '0 4px 12px rgba(59,130,246,0.3)'
  },
  title: { margin: 0, fontSize: '22px', fontWeight: '800', color: '#f8fafc', letterSpacing: '-0.02em', textShadow: '0 1px 2px rgba(0,0,0,0.2)' },
  subtitle: { fontSize: '13px', color: '#94a3b8', marginTop: '2px' },
  
  viewToggle: { 
    display: 'flex', 
    borderRadius: '10px', 
    overflow: 'hidden', 
    border: '1px solid #475569',
    backgroundColor: '#1e293b',
    padding: '3px',
    boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.2)'
  },
  viewToggleBtn: { 
    padding: '8px 12px', 
    border: 'none', 
    cursor: 'pointer', 
    borderRadius: '7px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s ease',
    fontWeight: '500'
  },
  createButton: { 
    padding: '10px 18px', 
    borderRadius: '10px', 
    border: 'none', 
    background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
    color: '#fff', 
    fontSize: '14px', 
    fontWeight: '600', 
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    boxShadow: '0 4px 12px rgba(59,130,246,0.4)',
    transition: 'all 0.2s ease'
  },
  importButton: { 
    padding: '10px 18px', 
    borderRadius: '10px', 
    border: '1px solid #475569', 
    backgroundColor: '#1e293b', 
    color: '#e2e8f0', 
    fontSize: '14px', 
    fontWeight: '500', 
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    transition: 'all 0.2s ease'
  },
  
  // v0.5.105: Professional Dark Search Container
  searchContainer: { 
    display: 'flex', 
    gap: '10px', 
    padding: '14px 20px', 
    borderBottom: '1px solid #334155', 
    background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)'
  },
  searchInput: { 
    flex: 1, 
    padding: '12px 16px', 
    borderRadius: '10px', 
    border: '1px solid #475569', 
    fontSize: '14px', 
    outline: 'none', 
    backgroundColor: '#0f172a',
    color: '#f8fafc',
    boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.2)',
    transition: 'border-color 0.2s, box-shadow 0.2s'
  },
  filterToggle: { 
    width: '44px', 
    height: '44px', 
    borderRadius: '10px', 
    border: '1px solid #475569', 
    cursor: 'pointer', 
    fontSize: '18px', 
    backgroundColor: '#1e293b',
    color: '#94a3b8',
    transition: 'all 0.2s ease'
  },
  // Filter Pills Container
  filterPillsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    padding: '14px 20px',
    background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
    borderBottom: '1px solid #334155'
  },
  filterPillsRow: {
    display: 'flex',
    gap: '10px',
    flexWrap: 'wrap',
    alignItems: 'center'
  },
  filterPill: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 16px',
    borderRadius: '24px',
    border: '2px solid',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    backgroundColor: 'transparent',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    letterSpacing: '0.3px'
  },
  filterPillCount: {
    fontSize: '11px',
    fontWeight: '700',
    backgroundColor: 'rgba(255,255,255,0.2)',
    padding: '2px 8px',
    borderRadius: '10px',
    minWidth: '20px',
    textAlign: 'center'
  },
  categoryLabel: {
    fontSize: '12px',
    color: '#64748b',
    fontWeight: '600',
    marginRight: '6px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  categoryPill: {
    padding: '8px 14px',
    borderRadius: '18px',
    border: '1px solid #475569',
    fontSize: '12px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
  },
  categoryPillCount: {
    fontSize: '10px',
    opacity: 0.8,
    fontWeight: '600'
  },
  importButton: {
    padding: '10px 14px',
    borderRadius: '10px',
    border: '1px solid #475569',
    backgroundColor: '#1e293b',
    color: '#94a3b8',
    fontSize: '14px',
    cursor: 'pointer',
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
  },
  
  mainContent: { flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' },
  
  // Carousel styles
  carouselContainer: { 
    flex: 1, 
    display: 'flex', 
    alignItems: 'center', 
    justifyContent: 'center',
    position: 'relative',
    padding: '20px 60px',
    overflow: 'hidden',
    backgroundColor: '#111827',
    background: 'linear-gradient(135deg, #1f2937 0%, #111827 100%)'
  },
  carouselArrow: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    border: 'none',
    backgroundColor: '#fff',
    color: '#1f2937',
    fontSize: '20px',
    cursor: 'pointer',
    zIndex: 20,
    boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'transform 0.2s, box-shadow 0.2s'
  },
  carouselTrack: {
    width: '100%',
    height: '100%',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative'
  },
  carouselInner: {
    position: 'relative',
    width: '100%',
    height: '420px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  carouselCard: {
    width: '340px',
    minWidth: '340px',
    backgroundColor: '#fff',
    borderRadius: '16px',
    overflow: 'visible', // v0.5.65: Allow badge to overflow
    boxShadow: '0 8px 32px rgba(0,0,0,0.2), 0 4px 12px rgba(0,0,0,0.1)',
    cursor: 'pointer',
    transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.4s ease, box-shadow 0.3s ease',
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
    position: 'relative' // v0.5.65: For absolute positioned badge
  },
  carouselCardHeader: {
    padding: '12px 16px',
    color: '#fff',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  carouselCardType: { fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.6px' },
  carouselCardCategory: { fontSize: '10px', opacity: 0.9, fontWeight: '500' },
  carouselCardBody: { padding: '14px', flex: 1 },
  carouselCardTitle: { margin: '0 0 12px 0', fontSize: '16px', fontWeight: '600', color: '#111827', letterSpacing: '-0.01em' },
  miniCourtPreview: {
    height: '180px',
    marginBottom: '12px',
    borderRadius: '10px'
  },
  carouselCardStats: { display: 'flex', gap: '14px', fontSize: '12px', color: '#64748b', justifyContent: 'center', fontWeight: '500' },
  carouselCardDate: { fontSize: '11px', color: '#9ca3af' },
  carouselCardFooter: {
    display: 'flex',
    justifyContent: 'space-around',
    padding: '14px',
    borderTop: '1px solid #e5e7eb',
    backgroundColor: '#f8fafc',
    borderRadius: '0 0 16px 16px'
  },
  carouselActionBtn: {
    padding: '8px 12px',
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    fontSize: '12px',
    color: '#374151',
    borderRadius: '6px',
    fontWeight: '500',
    transition: 'all 0.2s ease'
  },
  paginationDots: {
    position: 'absolute',
    bottom: '12px',
    left: '50%',
    transform: 'translateX(-50%)',
    display: 'flex',
    gap: '8px',
    alignItems: 'center'
  },
  paginationDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.2s ease'
  },
  
  // List view styles
  playsList: { flex: 1, overflowY: 'auto', padding: '8px' },
  emptyState: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 20px', color: '#6b7280', fontSize: '14px', gap: '8px', flex: 1 },
  emptyIcon: { fontSize: '48px', marginBottom: '8px' },
  emptyButtons: { display: 'flex', gap: '8px', marginTop: '12px' },
  emptyButton: { padding: '10px 16px', borderRadius: '6px', border: 'none', backgroundColor: '#3b82f6', color: '#fff', fontSize: '13px', cursor: 'pointer' },
  // v0.5.66: Professional Footer
  // v0.5.105: Professional Dark Footer
  footer: { 
    display: 'flex', 
    alignItems: 'center', 
    justifyContent: 'center',
    gap: '20px', 
    padding: '12px 20px', 
    background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
    borderTop: '1px solid #334155', 
    fontSize: '12px', 
    color: '#94a3b8'
  },
  footerNav: { display: 'flex', alignItems: 'center', gap: '8px' },
  footerHint: { color: '#64748b', fontSize: '12px', fontWeight: '500' },
  footerCounter: { fontWeight: '700', color: '#f8fafc', fontSize: '13px' },
  footerDivider: { width: '1px', height: '20px', backgroundColor: '#475569' },
  kbd: { 
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '4px 8px', 
    borderRadius: '6px', 
    backgroundColor: '#334155', 
    border: '1px solid #475569',
    fontSize: '11px',
    fontWeight: '600',
    color: '#f8fafc',
    fontFamily: 'ui-monospace, monospace',
    boxShadow: '0 2px 0 #1e293b, inset 0 1px 0 rgba(255,255,255,0.1)'
  },
  
  // Section styles (list view)
  section: { marginBottom: '8px', backgroundColor: '#fff', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
  sectionHeader: { display: 'flex', alignItems: 'center', padding: '10px 12px', cursor: 'pointer', backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' },
  sectionTitle: { fontWeight: '600', fontSize: '13px', color: '#374151' },
  sectionCount: { marginLeft: '8px', padding: '2px 8px', backgroundColor: '#e5e7eb', borderRadius: '10px', fontSize: '11px', color: '#6b7280' },
  collapseIcon: { marginLeft: 'auto', color: '#9ca3af', fontSize: '10px' },
  sectionContent: { padding: '8px' },
  
  // Play card styles (list view)
  playCard: { display: 'flex', alignItems: 'center', padding: '10px 12px', borderRadius: '6px', cursor: 'pointer', marginBottom: '4px', transition: 'all 0.15s', backgroundColor: '#fff', border: '1px solid transparent', position: 'relative' },
  playCardSelected: { backgroundColor: '#f0fdf4', border: '1px solid #22c55e', boxShadow: '0 0 0 2px rgba(34,197,94,0.2)' },
  playInfo: { flex: 1 },
  playName: { fontWeight: '500', fontSize: '14px', color: '#1f2937', marginBottom: '2px' },
  playMeta: { display: 'flex', gap: '8px', fontSize: '12px' },
  playCategory: { fontSize: '11px', padding: '2px 6px', borderRadius: '4px', fontWeight: '500' },
  playDate: { color: '#9ca3af' },
  courtIndicator: { width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px' },
  playActions: { display: 'flex', gap: '4px' },
  actionButton: { width: '28px', height: '28px', borderRadius: '4px', border: 'none', backgroundColor: '#f3f4f6', cursor: 'pointer', fontSize: '12px' },
  
  // Modal styles
  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { backgroundColor: '#fff', borderRadius: '12px', width: '400px', maxWidth: '90%', maxHeight: '80vh', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px', borderBottom: '1px solid #e5e7eb' },
  modalTitle: { margin: 0, fontSize: '16px', fontWeight: '600' },
  modalClose: { width: '32px', height: '32px', borderRadius: '6px', border: 'none', backgroundColor: '#f3f4f6', cursor: 'pointer', fontSize: '16px' },
  modalBody: { padding: '16px' },
  modalDesc: { margin: '0 0 16px', color: '#6b7280', fontSize: '13px' },
  uploadButton: { width: '100%', padding: '12px', borderRadius: '6px', border: '2px dashed #d1d5db', backgroundColor: '#f9fafb', cursor: 'pointer', fontSize: '14px', marginBottom: '12px' },
  importTextarea: { width: '100%', height: '120px', padding: '12px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '12px', fontFamily: 'monospace', resize: 'none', boxSizing: 'border-box' },
  importError: { marginTop: '8px', padding: '8px', backgroundColor: '#fee2e2', color: '#dc2626', borderRadius: '6px', fontSize: '12px' },
  modalFooter: { display: 'flex', justifyContent: 'flex-end', gap: '8px', padding: '16px', borderTop: '1px solid #e5e7eb' },
  cancelButton: { padding: '8px 16px', borderRadius: '6px', border: '1px solid #d1d5db', backgroundColor: '#fff', cursor: 'pointer', fontSize: '13px' },
  importConfirmButton: { padding: '8px 16px', borderRadius: '6px', border: 'none', backgroundColor: '#3b82f6', color: '#fff', cursor: 'pointer', fontSize: '13px', fontWeight: '500' },
  
  // Grid view styles - carousel-style cards in grid layout
  gridContainer: {
    flex: 1,
    overflowY: 'auto',
    padding: '16px 20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '28px'
  },
  gridSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  },
  gridSectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    paddingBottom: '10px',
    borderBottom: '1px solid #334155'
  },
  gridSectionTitle: {
    fontSize: '15px',
    fontWeight: '700',
    letterSpacing: '0.5px'
  },
  gridSectionCount: {
    fontSize: '12px',
    color: '#64748b',
    backgroundColor: 'rgba(100,116,139,0.2)',
    padding: '3px 10px',
    borderRadius: '12px',
    fontWeight: '600'
  },
  gridCards: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: '20px'
  },
  // Grid Play Card - matches carousel card style
  gridPlayCard: {
    position: 'relative',
    backgroundColor: '#fff',
    borderRadius: '14px',
    overflow: 'hidden',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.06)',
    border: '2px solid transparent',
    cursor: 'pointer',
    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
    display: 'flex',
    flexDirection: 'column'
  },
  gridPlayCardHeader: {
    padding: '12px 16px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  gridPlayCardType: {
    fontSize: '11px',
    fontWeight: '700',
    color: '#fff',
    textTransform: 'uppercase',
    letterSpacing: '0.6px'
  },
  gridPlayCardCategory: {
    fontSize: '10px',
    fontWeight: '600',
    color: 'rgba(255,255,255,0.9)',
    backgroundColor: 'rgba(255,255,255,0.25)',
    padding: '4px 10px',
    borderRadius: '8px',
    backdropFilter: 'blur(4px)'
  },
  gridPlayCardBody: {
    padding: '14px 16px 16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    flex: 1
  },
  gridPlayCardTitle: {
    margin: 0,
    fontSize: '16px',
    fontWeight: '600',
    color: '#111827',
    lineHeight: '1.35',
    letterSpacing: '-0.01em'
  },
  gridMiniCourtPreview: {
    width: '100%',
    height: '140px',
    backgroundColor: '#f8fafc',
    borderRadius: '10px',
    border: '1px solid #e2e8f0',
    boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.04)'
  },
  gridPlayCardStats: {
    display: 'flex',
    gap: '14px',
    fontSize: '12px',
    color: '#64748b',
    fontWeight: '500',
    paddingTop: '8px'
  },
  gridPlayCardActions: {
    position: 'absolute',
    bottom: '14px',
    right: '14px',
    display: 'flex',
    gap: '6px',
    backgroundColor: 'rgba(255,255,255,0.95)',
    padding: '6px',
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
  },
  gridActionBtn: {
    width: '32px',
    height: '32px',
    borderRadius: '6px',
    border: 'none',
    backgroundColor: '#f3f4f6',
    cursor: 'pointer',
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background-color 0.15s'
  }
};

export default PlayLibrary;
